/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import type { User, Role } from '../types';
import { loginRequest } from '../config/auth';

const API_BASE = import.meta.env.VITE_API_URL || '';

function getMockUser(): User {
  return {
    email: import.meta.env.VITE_LOCAL_USER_EMAIL || 'dev@localhost',
    name: import.meta.env.VITE_LOCAL_USER_NAME || 'Local Developer',
    role: (import.meta.env.VITE_LOCAL_USER_ROLE as Role) || 'admin',
  };
}

function isAuthBypassed(): boolean {
  return import.meta.env.VITE_AUTH_BYPASS === 'true';
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  getAccessToken: () => Promise<string | null>;
  signIn: () => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function MsalAuthProvider({ children }: { children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (isAuthBypassed()) return 'mock-token';

    const account = accounts[0];
    if (!account) return null;

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      return response.idToken;
    } catch {
      // Silent token acquisition failed — user needs to re-authenticate
      return null;
    }
  }, [instance, accounts]);

  // Fetch user profile from /me after MSAL authentication
  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;

    if (!isMsalAuthenticated) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchUserProfile() {
      try {
        const token = await getAccessToken();
        if (!token || cancelled) return;

        const response = await fetch(`${API_BASE}/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error('Failed to fetch user profile');

        const data = await response.json();
        if (!cancelled) {
          setUser({
            email: data.email,
            name: data.name,
            role: data.role,
          });
        }
      } catch (err) {
        console.error('[AuthContext] Failed to fetch /me:', err);
        // Fall back to MSAL account info with basic-user role
        const account = accounts[0];
        if (account && !cancelled) {
          setUser({
            email: account.username,
            name: account.name || account.username,
            role: 'basic-user',
          });
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchUserProfile();
    return () => { cancelled = true; };
  }, [isMsalAuthenticated, inProgress, accounts, getAccessToken]);

  const signIn = useCallback(() => {
    instance.loginRedirect(loginRequest).catch((err) => {
      console.error('[AuthContext] Login failed:', err);
    });
  }, [instance]);

  const signOut = useCallback(() => {
    setUser(null);
    instance.logoutRedirect().catch((err) => {
      console.error('[AuthContext] Logout failed:', err);
    });
  }, [instance]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user,
        isLoading,
        user,
        getAccessToken,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function BypassAuthProvider({ children }: { children: ReactNode }) {
  const mockUser = getMockUser();

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: true,
        isLoading: false,
        user: mockUser,
        getAccessToken: async () => 'mock-token',
        signIn: () => {},
        signOut: () => {},
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (isAuthBypassed()) {
    return <BypassAuthProvider>{children}</BypassAuthProvider>;
  }
  return <MsalAuthProvider>{children}</MsalAuthProvider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
